import React, { useEffect, useState } from 'react';
import Select from 'react-select';

// Base codes from the existing codes.js (same data as AssistanceSelector uses from _utils)
const BASE_CODES = [
    { label: "WCHR - Passenger can walk short distance up or down stairs", value: "WCHR" },
    { label: "WCHS - Passenger can walk short distances but not up or down stairs", value: "WCHS" },
    { label: "WCHC - Passenger cannot walk any distance and will require the aisle chair to board", value: "WCHC" },
    { label: "WCMP - Passenger is travelling with a manual wheelchair", value: "WCMP" },
    { label: "WCBD - Passenger is travelling with wheelchair with dry cell battery", value: "WCBD" },
    { label: "WCLB - Passenger is travelling with Lithium ion wheelchair", value: "WCLB" },
    { label: "DPNA - Disabled Passenger with acquired brain injury, intellectual or developmental disability", value: "DPNA" },
    { label: "DEAF - Passenger is deaf or hard of hearing", value: "DEAF" },
    { label: "BLND - Passenger is blind or has reduced vision", value: "BLND" },
    { label: "SVAN - Passenger is travelling with a service dog", value: "SVAN" },
    { label: "PPOC - Passenger is traveling with a portable oxygen concentrator", value: "PPOC" },
    { label: "ESAN - Passenger is traveling with an emotional support animal", value: "ESAN" },
    { label: "OXYG - Passenger will require oxygen", value: "OXYG" },
    { label: "MEDA - Medical Case", value: "MEDA" },
    { label: "MAAS - Meet and assist", value: "MAAS" },
];

// Airport-specific extra codes — mirrors AssistanceSelector logic
const EXTRA_CODES = {
    DUB: [{ label: 'LNYD - Hidden Disabilities', value: 'LNYD' }],
    BFS: [
        { label: 'MAAS - Meet and assist', value: 'MAAS' },
        { label: 'PETC - Pet in Cabin', value: 'PETC' },
    ],
};

/**
 * Redux-free version of AssistanceSelector for the public passenger check-in page.
 * Props:
 *   value        — string[]  selected codes e.g. ["WCHR", "BLND"]
 *   setAssistance — (string[]) => void  callback when selection changes
 *   iata         — string  airport code e.g. "DUB" — used to add airport-specific codes
 */
const PaxAssistanceSelector = ({ value = [], setAssistance, iata }) => {
    const [codes, setCodes] = useState(BASE_CODES);

    useEffect(() => {
        if (iata && EXTRA_CODES[iata]) {
            // Merge extra codes, avoiding duplicates by value
            const existing = new Set(BASE_CODES.map(c => c.value));
            const extras = EXTRA_CODES[iata].filter(c => !existing.has(c.value));
            setCodes([...BASE_CODES, ...extras]);
        } else {
            setCodes(BASE_CODES);
        }
    }, [iata]);

    // Convert string[] → { label, value }[] for react-select
    const transformToObjects = (vals) => {
        if (!vals || !vals.length) return [];
        return vals
            .map(v => codes.find(c => c.value === v))
            .filter(Boolean);
    };

    const handleChange = (selection) => {
        setAssistance((selection || []).map(s => s.value));
    };

    // Light/white styles — same as AssistanceSelector with isBooking=true
    const selectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '8px',
            backgroundColor: 'white',
            borderColor: '#dee2e6',
            minHeight: '44px',
        }),
        placeholder: (provided) => ({ ...provided, color: '#6c757d', fontSize: '0.88rem' }),
        menu: (provided) => ({ ...provided, backgroundColor: 'white', zIndex: 9999 }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#f0ebff' : 'white',
            color: '#212529',
            fontSize: '0.85rem',
            cursor: 'pointer',
        }),
        multiValue: (provided) => ({ ...provided, backgroundColor: '#ede9fe' }),
        multiValueLabel: (provided) => ({ ...provided, color: '#6f42c1', fontWeight: 600, fontSize: '0.8rem' }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#6f42c1',
            ':hover': { backgroundColor: '#6f42c1', color: 'white' },
        }),
    };

    return (
        <Select
            isMulti
            name="assistance"
            options={codes}
            className="basic-multi-select"
            classNamePrefix="select"
            placeholder="Select assistance codes…"
            onChange={handleChange}
            value={transformToObjects(value)}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPlacement="auto"
            menuPosition="fixed"
            styles={selectStyles}
        />
    );
};

export default PaxAssistanceSelector;
